/**
 * Getting Gramas onto a home screen.
 *
 * The app was built as an installable web app and shipped without ever
 * registering its service worker: `registerType: 'prompt'` means the plugin
 * writes `sw.js` but leaves the registration to the app, and nothing called it.
 * Chrome will not offer to install a site that has no service worker, so on
 * Android there was no install button anywhere, and offline never worked
 * either.
 *
 * Registration lives here rather than in `main.tsx` so the install banner and
 * the update prompt can share one source of truth about what the worker is
 * doing.
 */

import { registerSW } from 'virtual:pwa-register'
import { getMeta, setMeta } from '../db/dexie.js'

/** Chrome's install event. Not in lib.dom, and only Chromium fires it. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const VISITS_KEY = 'app.visits'
const DISMISSED_KEY = 'app.installDismissed'

let deferred: BeforeInstallPromptEvent | null = null
let needsRefresh = false
let updateSW: ((reload?: boolean) => Promise<void>) | null = null

type Listener = () => void
const listeners = new Set<Listener>()

function notify(): void {
  for (const listener of listeners) listener()
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Already launched from the home screen — nothing left to offer. */
export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

export function isIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

export function canPromptNatively(): boolean {
  return deferred != null
}

export function updateAvailable(): boolean {
  return needsRefresh
}

/** Take the offered update. Reloads, so nothing after this runs. */
export async function applyUpdate(): Promise<void> {
  await updateSW?.(true)
}

/**
 * Chromium's own install dialog. Returns false when the browser has nothing to
 * show — iOS Safari never fires the event, and there the UI explains the Share
 * menu instead, because Safari exposes no programmatic install at all.
 */
export async function promptInstall(): Promise<boolean> {
  const event = deferred
  if (!event) return false
  deferred = null
  notify()
  await event.prompt()
  const choice = await event.userChoice
  if (choice.outcome === 'dismissed') await dismissInstall()
  return choice.outcome === 'accepted'
}

export async function dismissInstall(): Promise<void> {
  await setMeta(DISMISSED_KEY, true)
  notify()
}

export interface InstallState {
  /** Show the banner: worth asking, and not asked away already. */
  eligible: boolean
  /** iOS has to be told where the button is; Chromium has a real dialog. */
  needsManualSteps: boolean
}

export async function readInstallState(): Promise<InstallState> {
  if (isStandalone()) return { eligible: false, needsManualSteps: false }
  const dismissed = await getMeta<boolean>(DISMISSED_KEY, false)
  const visits = await getMeta<number>(VISITS_KEY, 0)
  // Second visit, not the first: someone still deciding whether they want the
  // app should not be asked to keep it before they have used it once.
  return {
    eligible: !dismissed && visits >= 2 && (deferred != null || isIos()),
    needsManualSteps: deferred == null && isIos(),
  }
}

/**
 * Register the worker and start watching for an install opportunity.
 *
 * Called once at startup. The visit counter it bumps is what the banner waits
 * on, so it has to run before anything renders.
 */
export function initPwa(): void {
  void getMeta<number>(VISITS_KEY, 0).then((visits) => setMeta(VISITS_KEY, visits + 1))

  window.addEventListener('beforeinstallprompt', (event) => {
    // Keeping the event is the whole point: once the default banner is
    // suppressed, this object is the only way back to Chrome's install dialog.
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    notify()
  })

  window.addEventListener('appinstalled', () => {
    deferred = null
    void setMeta(DISMISSED_KEY, true)
    notify()
  })

  updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_url, registration) {
      if (!registration) return
      /**
       * Ask whether there is a new build, rather than waiting to be told.
       *
       * A service worker only checks for its own update on navigation, and an
       * installed web app is opened from the home screen and then lives in the
       * background for days — so without this a deploy can go unnoticed until
       * the phone decides to kill the tab. Checking on return to the foreground
       * is the one that matters; the interval covers an app left open.
       */
      const check = () => void registration.update().catch(() => {})
      const onVisible = () => {
        if (document.visibilityState === 'visible') check()
      }
      document.addEventListener('visibilitychange', onVisible)
      window.setInterval(check, 15 * 60 * 1000)
      check()
    },
    onNeedRefresh() {
      needsRefresh = true
      notify()
      // Reloading under someone's hands loses a half-filled sheet or a scan in
      // progress, so the banner asks. Hidden is the exception: nothing is being
      // typed into a backgrounded app, and taking the update now means the next
      // launch is simply current — which is the behaviour anyone expects and
      // the reason nobody should ever have to clear a cache by hand.
      if (document.visibilityState === 'hidden') void updateSW?.(true)
    },
  })
}
